// Platform-wide aggregate sentiment map (TRIL-327): the same heat renderer + glass
// HUD as the per-story map, but the data is every story combined, and the selected-
// region panel is a ranked, clickable list of the stories driving that region.
import './sentiment-map.scss';

import { useState } from 'react';
import { Link } from 'react-router';

import { STATE_NAME_BY_FIPS, stateFipsFromPath } from './geography';
import { rampColor } from './heatField';
import { SentimentHeatMap } from './SentimentHeatMap';
import type { SentimentRegion } from './types';

export interface PlatformRegion {
  level: number;
  path: string;
  sentiment: number | null;
  nReplies: number;
  nNegative: number | null;
  nNeutral: number | null;
  nPositive: number | null;
  stories: {
    storyId: string;
    title: string | null;
    score: number | null;
    sentiment: number | null;
    nReplies: number;
  }[];
}

export interface PlatformData {
  nRegions: number;
  nStories: number;
  computedAt: string;
  regions: PlatformRegion[];
}

interface Props {
  data: PlatformData | null;
  loading?: boolean;
  dark: boolean;
}

const fmt = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
// static legend swatches off the same ramp
const LEGEND = [-0.78, -0.38, -0.15, 0, 0.15, 0.38, 0.78];

export function PlatformSentimentMap({ data, loading, dark }: Props) {
  const [selectedFips, setSelectedFips] = useState<string | null>(null);
  const [hover, setHover] = useState<{
    name: string;
    x: number;
    y: number;
    flip: boolean;
  } | null>(null);
  const cls = `smap ${dark ? 'smap--dark' : 'smap--light'}`;

  if (loading) {
    return (
      <div className={cls}>
        <div className="smap__empty">Loading the platform map…</div>
      </div>
    );
  }

  const states =
    data?.regions.filter((r) => r.level === 1 && r.sentiment != null) ?? [];
  if (!data || states.length === 0) {
    return (
      <div className={cls}>
        <div className="smap__empty">
          <p className="smap__empty-title">No platform sentiment yet</p>
          <p className="smap__empty-sub">
            The platform map fills in as stories gather enough regional
            reactions. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  const heatRegions: SentimentRegion[] = states.map((r) => ({
    level: 1,
    path: r.path,
    sentiment: r.sentiment,
    sentimentVar: null,
    agreement: null,
    nReplies: r.nReplies,
    nVotes: 0,
    nNegative: r.nNegative,
    nNeutral: r.nNeutral,
    nPositive: r.nPositive,
    isOpposite: false,
    isLocalConsensus: false,
  }));

  const totalRx = states.reduce((a, r) => a + r.nReplies, 0);
  const natSentiment = totalRx
    ? states.reduce((a, r) => a + (r.sentiment ?? 0) * r.nReplies, 0) / totalRx
    : 0;

  const selectedName = selectedFips
    ? (STATE_NAME_BY_FIPS[selectedFips] ?? 'Selected state')
    : null;
  const selected = selectedFips
    ? (states.find((r) => stateFipsFromPath(r.path) === selectedFips) ?? null)
    : null;

  const split = (r: PlatformRegion) => {
    const total =
      (r.nNegative ?? 0) + (r.nNeutral ?? 0) + (r.nPositive ?? 0) || 1;
    return {
      neg: `${((r.nNegative ?? 0) / total) * 100}%`,
      neu: `${((r.nNeutral ?? 0) / total) * 100}%`,
      pos: `${((r.nPositive ?? 0) / total) * 100}%`,
    };
  };

  return (
    <div className={cls}>
      <div className="smap__stage">
        <SentimentHeatMap
          regions={heatRegions}
          dark={dark}
          selectedFips={selectedFips}
          onSelectState={setSelectedFips}
          onHoverState={setHover}
          allowedFips={null}
        />

        {hover && (
          <div
            className={`smap__hover ${hover.flip ? 'smap__hover--left' : ''}`}
            style={{ left: hover.x, top: hover.y }}
          >
            {hover.name}
          </div>
        )}

        <div className="smap__stack">
          <div className="smap__glass smap__summary">
            <span className="smap__region-name">All stories</span>
            <span className="smap__big">{fmt(natSentiment)}</span>
            <span className="smap__muted">
              {data.nStories.toLocaleString()}{' '}
              {data.nStories === 1 ? 'story' : 'stories'} ·{' '}
              {totalRx.toLocaleString()} reactions
            </span>
          </div>

          {selectedName && (
            <div className="smap__glass smap__region">
              <span className="smap__region-name">{selectedName}</span>
              {selected ? (
                <>
                  <span className="smap__big">
                    {selected.sentiment == null ? '—' : fmt(selected.sentiment)}
                  </span>
                  <span className="smap__split" aria-hidden>
                    <span
                      className="smap__split-neg"
                      style={{ width: split(selected).neg }}
                    />
                    <span
                      className="smap__split-neu"
                      style={{ width: split(selected).neu }}
                    />
                    <span
                      className="smap__split-pos"
                      style={{ width: split(selected).pos }}
                    />
                  </span>
                  <span className="smap__muted">
                    {selected.nReplies.toLocaleString()} reactions across{' '}
                    {selected.stories.length}{' '}
                    {selected.stories.length === 1 ? 'story' : 'stories'}
                  </span>
                  <div className="smap__stories">
                    {selected.stories.map((s) => (
                      <Link
                        key={s.storyId}
                        to={`/post/${s.storyId}`}
                        className="smap__story"
                      >
                        <span className="smap__story-title">
                          {s.title || 'Untitled story'}
                        </span>
                        <span className="smap__story-meta">
                          {s.score != null ? `${s.score.toFixed(0)}` : '—'} ·{' '}
                          {s.sentiment == null ? '—' : fmt(s.sentiment)}
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <span className="smap__muted">No stories mapped here yet</span>
              )}
            </div>
          )}
        </div>

        <div className="smap__glass smap__legend">
          <span className="smap__band-chips">
            {LEGEND.map((v) => {
              const [r, g, b] = rampColor(v, dark);
              return (
                <span
                  key={v}
                  className="smap__band"
                  style={{ background: `rgb(${r}, ${g}, ${b})` }}
                />
              );
            })}
          </span>
          <span className="smap__muted">negative → positive</span>
        </div>

        <div className="smap__glass smap__crumb">
          <button
            type="button"
            className="smap__crumb-link"
            onClick={() => setSelectedFips(null)}
            disabled={!selectedFips}
          >
            All stories
          </button>
          {selectedName ? (
            <span>&nbsp;▸ {selectedName}</span>
          ) : (
            <span className="smap__muted">&nbsp;·&nbsp; click a state</span>
          )}
        </div>
      </div>
    </div>
  );
}
